// @ts-nocheck
import React, { useState } from 'react';
import { AlertTriangle, Calendar, DollarSign, Clock, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { EditAdministrativeProcessModal } from '@/components/cases/EditAdministrativeProcessModal';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PendingPayment {
  id: string;
  processNumber: string;
  clientName: string;
  paymentType: 'benefit' | 'initial_fee' | 'success_fee';
  amount: number;
  dueDate: Date | null;
  description: string;
  priority: 'high' | 'medium' | 'low';
}

interface PendingPaymentAlertsProps {
  pendingPayments: PendingPayment[];
  onMarkAsPaid?: (paymentId: string) => void;
  onViewDetails?: (paymentId: string) => void;
  onProcessUpdate?: () => void;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const getPaymentTypeLabel = (type: string): string => {
  switch (type) {
    case 'benefit':
      return '💰 Benefício';
    case 'initial_fee':
      return '🏁 Hon. Iniciais';
    case 'success_fee':
      return '🎯 Hon. Êxito';
    default:
      return '💵 Pagamento';
  }
};

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'high':
      return 'bg-red-100 border-red-300 text-red-800';
    case 'medium':
      return 'bg-yellow-100 border-yellow-300 text-yellow-800';
    case 'low':
      return 'bg-blue-100 border-blue-300 text-blue-800';
    default:
      return 'bg-gray-100 border-gray-300 text-gray-800';
  }
};

const getDaysUntilDue = (dueDate: Date | null): number => {
  if (!dueDate) return -999; // Valor especial para datas pendentes
  const today = new Date();
  const diffTime = dueDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const getStatusText = (dueDate: Date | null): string => {
  if (!dueDate) return '⚠️ Data Pendente';
  const days = getDaysUntilDue(dueDate);
  if (days < 0) return `🔴 Vencido há ${Math.abs(days)} dias`;
  if (days === 0) return '🟡 Vence hoje';
  if (days <= 3) return `🟠 Vence em ${days} dias`;
  return `🟢 Vence em ${days} dias`;
};

export const PendingPaymentAlerts: React.FC<PendingPaymentAlertsProps> = ({
  pendingPayments,
  onMarkAsPaid,
  onViewDetails,
  onProcessUpdate,
}) => {
  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(null);
  const [processDetails, setProcessDetails] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleInformDate = async (payment: PendingPayment) => {
    setLoading(true);
    try {
      // Buscar dados completos do processo administrativo
      const { data: processData, error } = await supabase
        .from('administrative_processes')
        .select('*')
        .eq('id', payment.administrativeProcessId || payment.id)
        .single();

      if (error) {
        toast({ title: "Erro", description: "Não foi possível carregar os dados do processo.", variant: "destructive" });
        return;
      }

      setProcessDetails(processData);
      setSelectedProcessId(payment.id);
      setIsEditModalOpen(true);
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleProcessUpdate = () => {
    // Recarregar dados após atualização do processo
    if (onProcessUpdate) {
      onProcessUpdate();
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedProcessId(null);
    setProcessDetails(null);
  };

  // Êxito não ocorreu (ex.: improcedente): marca no processo e o alerta deixa de aparecer
  const [confirmingLostId, setConfirmingLostId] = useState<string | null>(null);
  const handleMarkLost = async (payment: PendingPayment) => {
    setLoading(true);
    try {
      const processId = payment.administrativeProcessId || payment.id;
      const { data: processData, error: fetchError } = await supabase
        .from('administrative_processes')
        .select('fee_configuration')
        .eq('id', processId)
        .single();
      if (fetchError) throw fetchError;

      // Remove o honorário de êxito da configuração do processo (fica o registro de quando foi excluído)
      const fee_configuration = {
        ...(processData?.fee_configuration || {}),
        hasSuccessFee: false,
        benefitFirstPaymentDatePending: false,
        successFeeLost: true,
        successFeeLostAt: new Date().toISOString(),
        successFeeLostReason: 'Êxito não ocorreu (ex.: pedido improcedente)',
      };
      const { error } = await supabase.from('administrative_processes').update({ fee_configuration }).eq('id', processId);
      if (error) throw error;

      // Exclui os lançamentos de êxito ainda não pagos desse processo
      const { error: deleteError } = await supabase
        .from('financial_entries')
        .delete()
        .eq('administrative_process_id', processId)
        .neq('status', 'Pago')
        .or('is_conditional_payment.eq.true,description.ilike.%xito%');
      if (deleteError) throw deleteError;

      toast({ title: 'Honorário de êxito excluído', description: `${payment.clientName}: não será mais cobrado.` });
      setConfirmingLostId(null);
      handleProcessUpdate();
    } catch (err: any) {
      toast({ title: 'Não foi possível atualizar', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (pendingPayments.length === 0) {
    return null;
  }

  const total = pendingPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white animate-in fade-in slide-in-from-bottom-1 duration-300">
      <div className="relative overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-5 py-6 sm:px-6">
        <img
          src="/imgs/logos allana/letra_A_logo.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -right-3 top-1/2 h-32 w-32 -translate-y-1/2 select-none object-contain opacity-[0.12]"
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
              <Calendar className="h-6 w-6" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Honorários de êxito sem data</p>
              <p className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
                {pendingPayments.length} {pendingPayments.length === 1 ? 'processo aguardando' : 'processos aguardando'}
              </p>
              <p className="mt-1 text-sm text-slate-500">Informe a data do primeiro recebimento ou exclua se o êxito não ocorreu.</p>
            </div>
          </div>
          <div className="sm:text-right">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">Total previsto</p>
            <p className="text-2xl font-semibold tabular-nums tracking-tight text-emerald-600">{formatCurrency(total)}</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {pendingPayments.map((payment) => {
          const confirming = confirmingLostId === payment.id;
          return (
            <div key={payment.id} className="group flex flex-col gap-3 px-5 py-4 transition-colors hover:bg-[#faf7f2]/60 sm:flex-row sm:items-center">
              <span className="hidden h-10 w-1 shrink-0 rounded-full bg-amber-400 sm:block" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="truncate text-sm font-semibold text-slate-900">{payment.clientName}</span>
                  <span className="font-mono text-xs text-slate-400">{payment.processNumber}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-slate-500" title={payment.description}>
                  {payment.description}
                </p>
              </div>

              <span className="shrink-0 text-sm font-semibold tabular-nums text-emerald-600">+ {formatCurrency(payment.amount)}</span>

              {confirming ? (
                <div className="flex shrink-0 items-center gap-1.5 rounded-lg bg-red-50 px-2 py-1">
                  <span className="text-xs text-red-700">Excluir o êxito? Não será cobrado.</span>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleMarkLost(payment)}
                    className="rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    Excluir
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingLostId(null)}
                    className="rounded-md px-2 py-1 text-xs text-slate-500 hover:text-slate-800"
                  >
                    Voltar
                  </button>
                </div>
              ) : (
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleInformDate(payment)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#c4ab83]/60 px-3 text-xs font-semibold text-[#7a6440] transition-colors hover:bg-[#c4ab83]/10 disabled:opacity-50"
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    Informar data
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmingLostId(payment.id)}
                    className="inline-flex h-8 items-center rounded-lg px-2.5 text-xs font-medium text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                    title="O êxito não ocorreu (ex.: improcedente): exclui este honorário"
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Excluir
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal de Edição */}
      {isEditModalOpen && processDetails && (
        <EditAdministrativeProcessModal
          processDetails={processDetails}
          isOpen={isEditModalOpen}
          onOpenChange={(open) => {
            setIsEditModalOpen(open);
            if (!open) {
              setSelectedProcessId(null);
              setProcessDetails(null);
            }
          }}
          onSuccess={() => {
            handleCloseModal();
            handleProcessUpdate();
          }}
        />
      )}
    </div>
  );
};

export default PendingPaymentAlerts;
